// components/Header/Header.js
import React, { useState, useEffect } from 'react';
import { 
  Menu, 
  X, 
  ChevronDown,
  Phone,
  Mail,
  MapPin,
  Clock,
  ExternalLink,
  Download,
  Play,
  Users,
  Award,
  Shield,
  Zap
} from 'lucide-react';
import './Header.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Enhanced navigation items with dropdowns
  const navigationItems = [
    { 
      name: 'Home', 
      href: '#home',
      type: 'link'
    },
    { 
      name: 'About', 
      href: '#about',
      type: 'link'
    },
    {
      name: 'Solutions',
      href: '#solutions',
      type: 'dropdown',
      items: [
        {
          name: 'ERP for Education',
          href: '#erp-education',
          icon: <Users size={16} />,
          description: 'Complete school management system'
        },
        {
          name: 'Facial Recognition',
          href: '#facial-recognition',
          icon: <Shield size={16} />,
          description: 'AI-powered attendance system'
        },
        {
          name: 'RFID Solutions',
          href: '#rfid-solutions',
          icon: <Zap size={16} />,
          description: 'Advanced RFID attendance'
        },
        {
          name: 'Custom Development',
          href: '#custom-development',
          icon: <Award size={16} />,
          description: 'Tailored software solutions'
        }
      ]
    },
    { 
      name: 'Features', 
      href: '#features',
      type: 'link'
    },
    {
      name: 'Resources',
      href: '#resources',
      type: 'dropdown',
      items: [
        {
          name: 'Documentation',
          href: '#documentation',
          icon: <ExternalLink size={16} />,
          description: 'Technical guides and APIs'
        },
        {
          name: 'Case Studies',
          href: '#case-studies',
          icon: <Award size={16} />,
          description: 'Success stories'
        },
        {
          name: 'Downloads',
          href: '#downloads',
          icon: <Download size={16} />,
          description: 'Brochures and resources'
        },
        {
          name: 'Video Demos',
          href: '#demos',
          icon: <Play size={16} />,
          description: 'Product demonstrations'
        }
      ]
    },
    { 
      name: 'Contact', 
      href: '#contact',
      type: 'link'
    }
  ];

  // Contact information for header
  const headerContact = [
    { icon: <Phone size={14} />, text: '+91 91016 33479', action: 'tel:+919101633479' },
    { icon: <Mail size={14} />, text: 'info@rootsofnortheast.com', action: 'mailto:info@rootsofnortheast.com' },
    { icon: <Clock size={14} />, text: 'Mon-Fri 9AM-6PM', action: null }
  ];

  const toggleMobileMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    setActiveDropdown(null);
  };

  const handleDropdownToggle = (itemName) => {
    if (activeDropdown === itemName) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(itemName);
    }
  };

  const handleDropdownItemClick = (href) => {
    setActiveDropdown(null);
    setIsMenuOpen(false);
    // Add navigation logic here
    console.log(`Navigate to: ${href}`);
  };

  const handleContactClick = (action) => {
    if (action) {
      window.open(action, '_self');
    }
  };

  const handleLogoClick = () => {
    // Smooth scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      {/* Top Bar */}
      <div className="header__topbar" role="complementary" aria-label="Contact information">
        <div className="header__topbar-container">
          <div className="header__topbar-left">
            <div className="header__topbar-contacts">
              {headerContact.map((contact, index) => (
                <div 
                  key={index} 
                  className="header__topbar-contact"
                  onClick={() => handleContactClick(contact.action)}
                  style={{ cursor: contact.action ? 'pointer' : 'default' }}
                  role={contact.action ? 'button' : 'text'}
                  tabIndex={contact.action ? 0 : -1}
                  onKeyDown={(e) => {
                    if (contact.action && (e.key === 'Enter' || e.key === ' ')) {
                      handleContactClick(contact.action);
                    }
                  }}
                >
                  <span aria-hidden="true">{contact.icon}</span>
                  <span>{contact.text}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="header__topbar-right">
            <div className="header__topbar-cta">
              <button className="header__topbar-button" aria-label="Request free consultation for ERP systems">
                <Download size={14} aria-hidden="true" />
                <span>Free Consultation</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className={`header ${scrollY > 50 ? 'header--scrolled' : ''}`} role="navigation" aria-label="Main navigation">
        <div className="header__container">
          <div className="header__content">
            {/* Enhanced Logo */}
            <div 
              className="header__logo" 
              onClick={handleLogoClick}
              role="button"
              tabIndex={0}
              aria-label="Roots of Northeast (ROne) - Go to homepage"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleLogoClick();
                }
              }}
            >
              <div className="header__logo-container">
                {!logoError ? (
                  <img 
                    src={process.env.PUBLIC_URL + "/new_logo.png"} 
                    alt="Roots of Northeast (ROne) - ERP Systems & AI Technology Solutions" 
                    className="header__logo-image"
                    width="105"
                    height="105"
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <div className="header__logo-fallback">
                    <span className="header__logo-fallback-text">R</span>
                  </div>
                )}
                <div className="header__logo-text">
                  <span className="header__logo-title">Roots of Northeast (ROne)</span>
                  <span className="header__logo-tagline">Technology Solutions</span>
                </div>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <div className="header__nav header__nav--desktop">
              {navigationItems.map((item) => (
                <div key={item.name} className="header__nav-item">
                  {item.type === 'dropdown' ? (
                    <div className="header__dropdown">
                      <button 
                        className="header__nav-link header__nav-link--dropdown"
                        onClick={() => handleDropdownToggle(item.name)}
                        onMouseEnter={() => setActiveDropdown(item.name)}
                      >
                        <span>{item.name}</span>
                        <ChevronDown 
                          className={`header__dropdown-icon ${activeDropdown === item.name ? 'header__dropdown-icon--open' : ''}`} 
                          size={16} 
                        />
                      </button>
                      <div 
                        className={`header__dropdown-menu ${activeDropdown === item.name ? 'header__dropdown-menu--open' : ''}`}
                        onMouseLeave={() => setActiveDropdown(null)}
                      >
                        <div className="header__dropdown-content">
                          {item.items.map((dropdownItem, index) => (
                            <div 
                              key={index}
                              className="header__dropdown-item"
                              onClick={() => handleDropdownItemClick(dropdownItem.href)}
                            >
                              <div className="header__dropdown-item-icon">
                                {dropdownItem.icon}
                              </div>
                              <div className="header__dropdown-item-content">
                                <div className="header__dropdown-item-title">
                                  {dropdownItem.name}
                                </div>
                                <div className="header__dropdown-item-description">
                                  {dropdownItem.description}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <a href={item.href} className="header__nav-link">
                      {item.name}
                    </a>
                  )}
                </div>
              ))}
              
              {/* CTA Buttons */}
              <div className="header__cta-buttons">
                <button className="header__cta-button header__cta-button--secondary">
                  <Play size={16} />
                  <span>Watch Demo</span>
                </button>
                <button className="header__cta-button header__cta-button--primary">
                  <span>Get Started</span>
                </button>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="header__mobile-toggle"
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          <div className={`header__nav header__nav--mobile ${isMenuOpen ? 'header__nav--mobile-open' : ''}`}>
            <div className="header__mobile-content">
              {navigationItems.map((item) => (
                <div key={item.name} className="header__mobile-item">
                  {item.type === 'dropdown' ? (
                    <div className="header__mobile-dropdown">
                      <button 
                        className="header__nav-link header__nav-link--mobile header__nav-link--dropdown-mobile"
                        onClick={() => handleDropdownToggle(item.name)}
                      >
                        <span>{item.name}</span>
                        <ChevronDown 
                          className={`header__dropdown-icon ${activeDropdown === item.name ? 'header__dropdown-icon--open' : ''}`} 
                          size={16} 
                        />
                      </button>
                      <div className={`header__mobile-dropdown-content ${activeDropdown === item.name ? 'header__mobile-dropdown-content--open' : ''}`}>
                        {item.items.map((dropdownItem, index) => (
                          <div 
                            key={index}
                            className="header__mobile-dropdown-item"
                            onClick={() => handleDropdownItemClick(dropdownItem.href)}
                          >
                            <div className="header__mobile-dropdown-item-icon">
                              {dropdownItem.icon}
                            </div>
                            <div className="header__mobile-dropdown-item-content">
                              <div className="header__mobile-dropdown-item-title">
                                {dropdownItem.name}
                              </div>
                              <div className="header__mobile-dropdown-item-description">
                                {dropdownItem.description}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <a 
                      href={item.href} 
                      className="header__nav-link header__nav-link--mobile"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item.name}
                    </a>
                  )}
                </div>
              ))}
              
              {/* Mobile CTA Buttons */}
              <div className="header__mobile-cta">
                <button className="header__cta-button header__cta-button--secondary header__cta-button--mobile">
                  <Play size={16} />
                  <span>Watch Demo</span>
                </button>
                <button className="header__cta-button header__cta-button--primary header__cta-button--mobile">
                  <span>Get Started</span>
                </button>
              </div>

              {/* Mobile Contact Info */}
              <div className="header__mobile-contact">
                <div className="header__mobile-contact-title">Contact Us</div>
                <div className="header__mobile-contact-items">
                  {headerContact.slice(0, 2).map((contact, index) => (
                    <div 
                      key={index} 
                      className="header__mobile-contact-item"
                      onClick={() => handleContactClick(contact.action)}
                    >
                      {contact.icon}
                      <span>{contact.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && <div className="header__overlay" onClick={() => setIsMenuOpen(false)} />}
    </>
  );
};

export default Header;